import { memo, useMemo } from "react";

import { MAINTENANCE_STATUS_OPTIONS } from "../constants/maintenance.constants";
import { maintenanceMessages } from "../maintenance.intl";

import { useMaintenanceHistoryColumns } from "./MaintenanceHistoryColumns";

import { useIntlMessages } from "@/app/hooks/useIntlMessages";
import { WpxTable, type FilterType } from "@/components/dataTable/WpxTable";
import type { ResponsePagination } from "@/components/pagination/hooks/usePagination";
import type { MaintenanceHistoryEntry } from "@/features/maintenance/types/maintenance.types";

interface MaintenanceHistoryTableProps {
  rows: MaintenanceHistoryEntry[];
  pagination: ResponsePagination;
  isLoading: boolean;
  onViewEntry: (id: string) => void;
  onEditEntry?: (entry: MaintenanceHistoryEntry) => void;
  onCancelEntry?: (id: string) => void;
}

const MaintenanceHistoryTableComponent = (
  props: MaintenanceHistoryTableProps,
): React.JSX.Element => {
  const {
    rows,
    pagination,
    isLoading,
    onViewEntry,
    onEditEntry,
    onCancelEntry,
  } = props;
  const t = useIntlMessages(maintenanceMessages);

  const columns = useMaintenanceHistoryColumns({
    onViewEntry,
    onEditEntry,
    onCancelEntry,
  });

  const filters = useMemo<FilterType<unknown, unknown>>(
    () => ({
      date: { placeholder: t("historyStartDate") },
      status: {
        label: t("historyStatus"),
        options: MAINTENANCE_STATUS_OPTIONS,
      },
    }),
    [],
  );

  const toolbar = useMemo(
    () => ({
      onRowClick: onViewEntry,
    }),
    [onViewEntry],
  );

  return (
    <WpxTable
      title={t("historyPageTitle")}
      rows={rows}
      columns={columns}
      filters={filters}
      toolbar={toolbar}
      hide={{ functions: false, filters: true }}
      pagination={pagination}
      isLoading={isLoading}
      empty={{
        subtitle: t("historyEmptySubtitle"),
      }}
    />
  );
};

export const MaintenanceHistoryTable = memo(MaintenanceHistoryTableComponent);
